'use client'

import { useContext } from 'react'
import { useIntl } from 'react-intl'

import { Label, Cell, Icon, Text, Button, Divider } from '@vinted/web-ui'
import { Plus24, Calendar24 } from '@vinted/monochrome-icons'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { GetShippingLabelContext } from '@marketplace-web/shipping-services-provider/shipping-services-provider-feature'

import {
  DropOffTypeKey,
  DropOffsPointType,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import { AddressModel } from '@marketplace-web/address/address-data'

import { getDropOffHour } from '../../utils'

type Props = {
  address: AddressModel | null
  pickUpValidation: string
  onClick: () => void
}

const PickupDate = ({ address, pickUpValidation, onClick }: Props) => {
  const translate = useTranslate('conversation.get_shipping_label.modal')
  const { formatDate } = useIntl()

  const {
    state: {
      collectionDates: { confirmedCollectionDate, confirmedCollectionTime },
      labelOptions: { labelOptions },
      dropOffPointTypeSelection: { selectedDropOffPointType },
    },
  } = useContext(GetShippingLabelContext)

  const labelOptionsDropOffs = labelOptions?.dropOffs
  const isHomePickupSelected = selectedDropOffPointType === DropOffsPointType.Home

  const isDropOffFlow = labelOptions?.dropOffTypes
    ?.map(({ pickUpDateShown }) => pickUpDateShown)
    .includes(true)
  const isDropOffsFlow =
    labelOptionsDropOffs &&
    labelOptionsDropOffs.length === 1 &&
    labelOptionsDropOffs[0]?.type === DropOffsPointType.Home

  const shouldShowPickupDate = isDropOffFlow || isHomePickupSelected || isDropOffsFlow

  if (!shouldShowPickupDate) return null

  const getIsContactlessDropoffFlow = () => {
    const dropOffTypes = labelOptions?.dropOffTypes

    return dropOffTypes?.some(({ key }) => key === DropOffTypeKey.ContactlessDropoff)
  }

  const labelText = getIsContactlessDropoffFlow()
    ? 'choose_drop_off_date.label'
    : 'choose_pickup_date.label'

  const renderPickUpDateChoice = () => {
    const titleText = getIsContactlessDropoffFlow()
      ? 'choose_drop_off_date.title'
      : 'choose_pickup_date.title'

    return (
      <>
        <Label type={Label.Type.Stacked} text={translate(labelText)} />
        <Cell
          type={Cell.Type.Navigating}
          title={translate(titleText)}
          suffix={<Icon name={Plus24} />}
          onClick={onClick}
          validation={pickUpValidation}
          testId="generate-label-modal-choose-pickup-date"
          disabled={getIsContactlessDropoffFlow() && !address}
          divider
        />
      </>
    )
  }

  const formatCollectionDay = (dayTimestamp?: string | null) => {
    if (!dayTimestamp) return ''

    // Need to remove +02:00 from the timestamp to correctly format the date
    const isoDate = dayTimestamp.split('T')[0]

    const date = new Date(`${isoDate!}T00:00:00Z`)

    const formattedDate = formatDate(date, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    })

    return formattedDate
  }

  const formatCollectionTime = (timeTimestamp?: string | null) => {
    if (!timeTimestamp) return ''

    const formattedTime = formatDate(timeTimestamp, {
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    })

    return formattedTime
  }

  const renderCollectionDateTitle = () =>
    getIsContactlessDropoffFlow()
      ? translate('choose_drop_off_date.selected_date', {
          date: formatCollectionDay(confirmedCollectionDate?.collection),
          time: getDropOffHour(confirmedCollectionDate?.dropOffTo),
        })
      : formatCollectionDay(confirmedCollectionDate?.collection)

  const renderCollectionDateSubtitle = () => {
    if (getIsContactlessDropoffFlow()) return null

    return (
      <Text
        as="h2"
        type={Text.Type.Title}
        text={`${formatCollectionTime(confirmedCollectionTime?.collection)} - ${formatCollectionTime(confirmedCollectionTime?.collectionTo)}`}
      />
    )
  }

  const renderCollectionDate = () => (
    <>
      <Label type={Label.Type.Stacked} text={translate(labelText)} />
      <Cell
        title={renderCollectionDateTitle()}
        body={renderCollectionDateSubtitle()}
        prefix={<Icon name={Calendar24} color={Icon.Color.GreyscaleLevel2} />}
        suffix={
          <Button
            size={Button.Size.Small}
            text={translate('choose_drop_off_date.action.change')}
            onClick={onClick}
            testId="generate-label-modal-collection-date-change-button"
          />
        }
        testId="generate-label-modal-collection-date"
        validation={pickUpValidation}
      />
      <Divider />
    </>
  )

  return confirmedCollectionDate ? renderCollectionDate() : renderPickUpDateChoice()
}

export default PickupDate
